import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Save, Trash2, Lock, ArrowRight } from 'lucide-react';
import { Dropzone } from './Dropzone';
import { RichTextEditor } from './RichTextEditor';
import HistoryDialog from './HistoryDialog';
import UpdatedAtBadge from './UpdatedAtBadge';
import DraftBanner from './DraftBanner';
import { useAutoSaveDraft } from '../../hooks/useAutoSaveDraft';

const defaultData = {
  id: 'about_modal',
  foundation_title: 'OUR FOUNDATION IS ALL ABOUT CHANGING LIVES',
  foundation_text:
    'The Educational, Welfare and Research Foundation (EWRF) is a Malaysian NPO & NGO, founded in 1979, whose mission is to empower marginalised communities in Malaysia by creating platforms for empowerment through education, psycho-social counseling and welfare for B40 communities.',
  foundation_objectives: [
    'Community development by empowering academically under performing and socially challenged students',
    'Increasing and sharing innovative approaches for information exchange through experimentation, research and technology',
    'Developing innovative platforms related to educational, social, counseling and welfare',
  ],
  core_programmes: [
    'Language for Juniors (L4J)',
    'Civil Society Intervention (CSI)',
    'Skill Training Initiative (STI)',
    'Volunteer Induction Programme (VIP)',
    'Counseling',
    'Research',
    'Medical Aid and Welfare',
  ],
  history_title: 'EWRF 46 year journey since 1979',
  history_text:
    'Here you will be able to trace back what started as a support for a few promising children by well intentioned professionals, over the years has become an esteemed organisation of dedicated citizens.',
  history_images: [],
  committee_title: 'The People Behind The Foundation',
  committee_subtitle: 'Currently serving Central Committee Members (2024-2026)',
  committee_text:
    'As per EWRF constitution, an election is conducted every two years to elect committee members.',
  committee_images: [],
  committee_members: [],
  past_presidents_title: 'Past Presidents',
  past_presidents_text: 'Honoring the leaders who have guided EWRF through the years',
  past_presidents_images: [],
  past_presidents_list: [],
  staff_title: 'Our Team',
  staff_text: 'Meet the dedicated staff members who make our programmes possible',
  staff_images: [],
  staff_members: [],
  // Public-page section headings
  foundation_eyebrow: 'Who We Are',
  foundation_heading_title: 'Our',
  foundation_heading_accent: 'Foundation',
  history_eyebrow: 'Our Journey',
  history_heading_title: 'Our',
  history_heading_accent: 'History',
  committee_eyebrow: 'The People Behind The Foundation',
  committee_heading_title: 'Central',
  committee_heading_accent: 'Committee',
  past_presidents_eyebrow: 'Our Legacy',
  past_presidents_heading_title: 'Past',
  past_presidents_heading_accent: 'Presidents',
  past_presidents_subtitle: 'Roster of past Presidents who shaped EWRF (1979 – 2024)',
  staff_eyebrow: 'Our Team',
  staff_heading_title: 'EWRF',
  staff_heading_accent: 'Staff',
  staff_subtitle: 'The dedicated professionals running our HQ operations day-to-day',
};

const linesToList = (text) => text.split('\n').map((s) => s.trim()).filter(Boolean);

const ImageList = ({ images, onChange, label }) => (
  <div className="space-y-3">
    <Label>{label}</Label>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {images.map((url, idx) => (
        <div key={idx} className="relative group">
          <img src={url} className="w-full h-32 object-cover rounded" alt="" />
          <button
            type="button"
            onClick={() => onChange(images.filter((_, i) => i !== idx))}
            className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded opacity-0 group-hover:opacity-100"
            data-testid={`remove-image-${idx}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
    <Dropzone
      compact
      multiple
      onUpload={(url) => onChange([...images, url])}
      label={`Add image to ${label}`}
    />
  </div>
);

const AboutModalManager = () => {
  const [data, setData] = useState(defaultData);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi.get('/about-modal');
        if (res.data) setData({ ...defaultData, ...res.data });
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const { hasDraft, draftAt, readDraft, discardDraft, markSaved } = useAutoSaveDraft({
    storageKey: 'draft:about_modal',
    data,
    enabled: loaded,
    savedAt: data.updated_at,
  });

  const restoreDraft = () => {
    const draft = readDraft();
    if (draft) setData({ ...defaultData, ...draft });
    discardDraft();
  };

  const save = async () => {
    if (!loaded) {
      toast({ title: 'Please wait', description: 'Content is still loading — try again in a moment.' });
      return;
    }
    setLoading(true);
    try {
      await adminApi.post('/about-modal', data);
      const res = await adminApi.get('/about-modal');
      if (res.data) setData({ ...defaultData, ...res.data });
      markSaved(res.data || data);
      toast({ title: 'Saved', description: 'About modal updated' });
    } catch (e) {
      toast({ title: 'Error', description: 'Save failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="about-modal-manager">
      {hasDraft && (
        <DraftBanner
          draftAt={draftAt}
          onRestore={restoreDraft}
          onDiscard={discardDraft}
          testid="about-modal-draft-banner"
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-2xl font-bold">About Modal</h2>
          <UpdatedAtBadge updatedAt={data.updated_at} />
        </div>
        <HistoryDialog
          collection="about_modal"
          docId="about_modal"
          onRestored={async () => {
            const res = await adminApi.get('/about-modal');
            if (res.data) setData({ ...defaultData, ...res.data });
          }}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Public Page Section Headings</CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Controls the coloured eyebrow, big title (with a gold accent word) and subtitle that appear above each About section on the public homepage — e.g. <b>Our Legacy → Past Presidents → Roster of past Presidents who shaped EWRF (1979 – 2024)</b>.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {[
            { key: 'foundation', label: 'Foundation Section' },
            { key: 'history', label: 'History Section' },
            { key: 'committee', label: 'Committee Section' },
            { key: 'past_presidents', label: 'Past Presidents Section' },
            { key: 'staff', label: 'Staff / Our Team Section' },
          ].map(({ key, label }) => {
            const hasSubtitle = key === 'past_presidents' || key === 'staff';
            return (
              <div key={key} className="border rounded-lg p-4 bg-gray-50" data-testid={`heading-block-${key}`}>
                <div className="font-semibold text-sm text-[#0A1628] mb-3">{label}</div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Eyebrow (small uppercase tag)</Label>
                    <Input
                      value={data[`${key}_eyebrow`] || ''}
                      onChange={(e) => setData({ ...data, [`${key}_eyebrow`]: e.target.value })}
                      data-testid={`${key}-eyebrow-input`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Heading — Title Word</Label>
                    <Input
                      value={data[`${key}_heading_title`] || ''}
                      onChange={(e) => setData({ ...data, [`${key}_heading_title`]: e.target.value })}
                      data-testid={`${key}-heading-title-input`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Heading — Accent Word (gold)</Label>
                    <Input
                      value={data[`${key}_heading_accent`] || ''}
                      onChange={(e) => setData({ ...data, [`${key}_heading_accent`]: e.target.value })}
                      data-testid={`${key}-heading-accent-input`}
                    />
                  </div>
                </div>
                {hasSubtitle && (
                  <div className="mt-3">
                    <Label className="text-xs">Subtitle (line below the heading)</Label>
                    <Input
                      value={data[`${key}_subtitle`] || ''}
                      onChange={(e) => setData({ ...data, [`${key}_subtitle`]: e.target.value })}
                      data-testid={`${key}-subtitle-input`}
                    />
                  </div>
                )}
                <div className="mt-3 text-xs text-gray-500">
                  Preview: <span className="uppercase tracking-widest text-amber-600 font-bold">{data[`${key}_eyebrow`]}</span>{' '}
                  → <span className="font-serif text-lg text-[#0A1628]">{data[`${key}_heading_title`]}</span>{' '}
                  <span className="font-serif italic text-lg text-[#D4AF37]">{data[`${key}_heading_accent`]}</span>
                  {hasSubtitle && data[`${key}_subtitle`] && (
                    <div className="text-gray-600 italic mt-1">{data[`${key}_subtitle`]}</div>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>About Modal — Foundation</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Section Title</Label>
            <Input value={data.foundation_title} onChange={(e) => setData({ ...data, foundation_title: e.target.value })} data-testid="foundation-title-input" />
          </div>
          <div>
            <Label>Body</Label>
            <RichTextEditor value={data.foundation_text} onChange={(v) => setData({ ...data, foundation_text: v })} />
          </div>
          <div>
            <Label>Objectives (one per line)</Label>
            <Textarea rows={4} value={data.foundation_objectives.join('\n')} onChange={(e) => setData({ ...data, foundation_objectives: linesToList(e.target.value) })} />
          </div>
          <div>
            <Label>Core Programmes (one per line)</Label>
            <Textarea rows={5} value={data.core_programmes.join('\n')} onChange={(e) => setData({ ...data, core_programmes: linesToList(e.target.value) })} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>About Modal — History (intro)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={data.history_title} onChange={(e) => setData({ ...data, history_title: e.target.value })} />
          </div>
          <div>
            <Label>Description</Label>
            <RichTextEditor value={data.history_text} onChange={(v) => setData({ ...data, history_text: v })} />
          </div>
          <LockedSectionNotice
            title="History Phases / Stages"
            tabName="History Stages"
            testid="lock-history-phases"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>About Modal — Committee Members</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={data.committee_title} onChange={(e) => setData({ ...data, committee_title: e.target.value })} />
          </div>
          <div>
            <Label>Subtitle</Label>
            <Input value={data.committee_subtitle} onChange={(e) => setData({ ...data, committee_subtitle: e.target.value })} />
          </div>
          <div>
            <Label>Description</Label>
            <RichTextEditor value={data.committee_text} onChange={(v) => setData({ ...data, committee_text: v })} minHeight={100} />
          </div>
          <LockedSectionNotice
            title="Committee Member Portraits"
            tabName="Committee"
            testid="lock-committee-members"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>About Modal — Past Presidents</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={data.past_presidents_title} onChange={(e) => setData({ ...data, past_presidents_title: e.target.value })} />
          </div>
          <div>
            <Label>Description</Label>
            <RichTextEditor value={data.past_presidents_text} onChange={(v) => setData({ ...data, past_presidents_text: v })} minHeight={100} />
          </div>
          <LockedSectionNotice
            title="Past President Portraits"
            tabName="Past Presidents"
            testid="lock-past-presidents"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>About Modal — Staff / Team</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={data.staff_title} onChange={(e) => setData({ ...data, staff_title: e.target.value })} />
          </div>
          <div>
            <Label>Description</Label>
            <RichTextEditor value={data.staff_text} onChange={(v) => setData({ ...data, staff_text: v })} minHeight={100} />
          </div>
          <LockedSectionNotice
            title="Team Portraits"
            tabName="Our Team"
            testid="lock-staff-members"
          />
        </CardContent>
      </Card>

      <Button onClick={save} disabled={loading || !loaded} className="bg-[#1565C0]" data-testid="save-about-modal-btn">
        <Save className="w-4 h-4 mr-2" />{!loaded ? 'Loading…' : loading ? 'Saving…' : 'Save About Modal'}
      </Button>
    </div>
  );
};

/** Small info card explaining that portrait arrays are locked to their dedicated tabs. */
const LockedSectionNotice = ({ title, tabName, testid }) => (
  <div
    className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
    data-testid={testid}
  >
    <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
    <div>
      <div className="font-semibold">{title} are managed in a dedicated tab</div>
      <div className="text-amber-800/90">
        To add, remove, edit, or reorder these portraits, open the{' '}
        <span className="font-semibold inline-flex items-center gap-1">
          &quot;{tabName}&quot; tab <ArrowRight className="w-3 h-3" />
        </span>{' '}
        in the Admin sidebar. This keeps them safe from accidental overwrites when saving other About Modal text.
      </div>
    </div>
  </div>
);

export default AboutModalManager;
